import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Eye, Trash2, Upload, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const ManageImports = () => {
  const navigate = useNavigate();
  const [masterData, setMasterData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewSampleData, setViewSampleData] = useState(null);
  const [viewSampleType, setViewSampleType] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/imports/masters-summary');
      setMasterData(response.data.masters || []);
    } catch (error) {
      console.error('Error fetching master data:', error);
      toast.error('Failed to fetch master data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewSample = async (masterType) => {
    try {
      const response = await apiClient.get(`/imports/masters-sample/${masterType}`);
      setViewSampleData(response.data.sample || []);
      setViewSampleType(masterType);
    } catch (error) {
      console.error('Error fetching sample:', error);
      toast.error('Failed to fetch sample data');
    }
  };

  const handleDelete = async (masterType) => {
    setIsDeleting(true);
    try {
      await apiClient.delete(`/imports/masters/${masterType}`);
      toast.success(`${masterType} data deleted successfully`);
      setDeleteConfirm(null);
      fetchMasterData();
    } catch (error) {
      console.error('Error deleting master:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete master data');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch {
      return dateStr;
    }
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '-';
    return num.toLocaleString('en-IN');
  };

  const totalImports = masterData.filter(m => m.record_count > 0).length;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Manage Imports</h1>
              <p className="text-sm text-[#E8DCC8]">View and manage imported master data</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate('/admin/import')}
              className="bg-[#2D5F4F] hover:bg-[#234A3D]"
            >
              <Upload className="w-4 h-4 mr-2" />
              New Import
            </Button>
            <Button
              onClick={fetchMasterData}
              variant="secondary"
              className="h-10"
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {/* Masters Table */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
          <div className="p-4 border-b border-[#E5DFD0] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#1A1F2E] font-display">Masters</h2>
            <span className="text-sm text-[#5A5F6E]">{totalImports} imports</span>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F1E8] hover:bg-[#F5F1E8]">
                  <TableHead className="font-semibold text-[#1A1F2E]">Import Type</TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E]">Period</TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E] text-right">Record Count</TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E]">Import Method</TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E]">Last Import</TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E] text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#5A5F6E]" />
                      <p className="text-sm text-[#5A5F6E] mt-2">Loading...</p>
                    </TableCell>
                  </TableRow>
                ) : masterData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-[#8A8F9E] py-8">
                      <FileText className="w-12 h-12 mx-auto text-[#E5DFD0] mb-3" />
                      <p>No imports yet. Upload CSV files to import data.</p>
                      <Button
                        onClick={() => navigate('/admin/import')}
                        variant="link"
                        className="mt-2 text-[#2D5F4F]"
                      >
                        Start your first import →
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  masterData.map((master) => (
                    <TableRow key={master.type} className="hover:bg-[#FFFBF5]">
                      <TableCell className="font-medium text-[#1A1F2E]">
                        {master.label}
                      </TableCell>
                      <TableCell className="text-[#8A8F9E]">
                        N/A
                      </TableCell>
                      <TableCell className="text-right font-semibold text-[#1A1F2E]">
                        {formatNumber(master.record_count)}
                      </TableCell>
                      <TableCell>
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Daily Replacement
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-[#5A5F6E]">
                        {formatDate(master.last_import)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewSample(master.type)}
                            className="h-9 px-3"
                            disabled={master.record_count === 0}
                            data-testid={`view-sample-${master.type}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Sample
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setDeleteConfirm(master)}
                            className="h-9 px-3"
                            disabled={master.record_count === 0}
                            data-testid={`delete-${master.type}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>

      {/* View Sample Dialog */}
      <Dialog open={!!viewSampleData} onOpenChange={() => setViewSampleData(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sample Data - {viewSampleType}</DialogTitle>
            <DialogDescription>
              Showing first 10 records
            </DialogDescription>
          </DialogHeader>
          
          {viewSampleData && viewSampleData.length > 0 ? (
            <div className="overflow-x-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    {Object.keys(viewSampleData[0]).map((key) => (
                      <TableHead key={key} className="font-semibold text-xs">
                        {key}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {viewSampleData.map((row, idx) => (
                    <TableRow key={idx}>
                      {Object.values(row).map((value, vidx) => (
                        <TableCell key={vidx} className="text-xs max-w-[200px] truncate">
                          {typeof value === 'object' ? JSON.stringify(value) : String(value || '-')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No sample data available</p>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewSampleData(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteConfirm?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all {formatNumber(deleteConfirm?.record_count)} records from {deleteConfirm?.label}. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteConfirm?.type)}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ManageImports;
